/**
 * The chart ACCESS TOKEN — the key to the private pack host (charts-gate).
 *
 * Licensed chart packs (Antares) live behind a token-checking Worker rather
 * than on an open URL like the public-domain NOAA set. The token is pasted
 * into the app's settings by the user and stored on the device; it is NEVER
 * baked into the build (docs/entitlement-design.md, "what I would not do" —
 * a bundled secret is obfuscation, not access control). When real accounts
 * exist, the entitlement service hands the app a short-lived credential and
 * this module is where it lands; nothing downstream changes.
 *
 * The token travels as a `?t=` query parameter appended to private-host URLs,
 * because the PMTiles reader's range requests can't reliably carry a header
 * through every path. One choke point does the appending: authedPackUrl.
 * Not a zustand store: nothing needs to re-render live off it except the
 * settings input, which keeps its own buffer.
 */
import { isPrivatePackUrl } from './packsConfig';

const LS_KEY = 'acp.chartaccess.token.v1';

let token: string | null = null;

/** The stored access token ('' when none). */
export function getChartAccessToken(): string {
  if (token !== null) return token;
  try {
    token = typeof localStorage !== 'undefined' ? (localStorage.getItem(LS_KEY) ?? '') : '';
  } catch {
    token = '';
  }
  return token;
}

/** Store a new token (trimmed; '' clears). */
export function setChartAccessToken(value: string): void {
  token = value.trim();
  try {
    if (typeof localStorage !== 'undefined') {
      if (token) localStorage.setItem(LS_KEY, token);
      else localStorage.removeItem(LS_KEY);
    }
  } catch {
    /* private mode / quota — the in-memory token still works this session */
  }
}

/**
 * Append the token as a query parameter. Pure and exported for tests; handles
 * a URL that already carries a query string.
 */
export function appendTokenParam(url: string, tok: string): string {
  if (!tok) return url;
  return `${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(tok)}`;
}

/**
 * The URL to actually fetch: unchanged for public hosts, token appended for
 * the private host. Central so every pack fetch path — manifest, download,
 * PMTiles range reads — goes through the same door the same way.
 */
export function authedPackUrl(url: string): string {
  if (!isPrivatePackUrl(url)) return url;
  return appendTokenParam(url, getChartAccessToken());
}
